<template>
  <div class="product__option product__description">
    <template v-if="productStore.isProductUINewVersion">
      <!-- Popup for desktop -->
      <ProductCollapsible
        :detailsHeader="`${themeData('productDescriptionTitle')} ${
          shouldLoadProductDescriptionAndShippingByVariantType &&
          selectedVariantStyleOptionValue
            ? `for ${selectedVariantStyleOptionValue}`
            : ''
        }`"
        class="hidden-md-down product-description-new-ui-desktop"
        @toggle="trackingProductDescriptionDetailsOpened"
      >
        <div class="product__description-short my-sm">
          <template v-if="shortDescription">
            <template v-for="(item, index) of shortDescription" :key="index">
              <span>{{ item.key }}</span>
              <span>{{ item.value }}</span>
            </template></template
          >
          <div
            v-else
            class="product__description-long"
            v-html="longDescription"
          ></div>
        </div>
        <div
          class="product__description-long"
          v-if="shortDescription"
          v-html="longDescription"
        ></div>
      </ProductCollapsible>

      <!-- Popup for mobile -->
      <LightBoxPopup
        group="product-description"
        contentId="product-description-popup-content"
        class="hidden-md-up"
      >
        <template #trigger>
          <span
            class="link hidden-md-up product-description-new-ui-mobile"
            @click="trackingProductDescriptionDetailsOpened(false)"
          >
            {{ themeData('productDescriptionTitle') }}
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle cx="12" cy="12" r="3.5" stroke="#333333"></circle>
              <path
                d="M20.188 10.9343C20.5762 11.4056 20.7703 11.6412 20.7703 12C20.7703 12.3588 20.5762 12.5944 20.188 13.0657C18.7679 14.7899 15.6357 18 12 18C8.36427 18 5.23206 14.7899 3.81197 13.0657C3.42381 12.5944 3.22973 12.3588 3.22973 12C3.22973 11.6412 3.42381 11.4056 3.81197 10.9343C5.23206 9.21014 8.36427 6 12 6C15.6357 6 18.7679 9.21014 20.188 10.9343Z"
                stroke="#333333"
              ></path>
            </svg>
            {{
              shouldLoadProductDescriptionAndShippingByVariantType &&
              selectedVariantStyleOptionValue
                ? `for ${selectedVariantStyleOptionValue}`
                : ''
            }}
          </span>
        </template>
        <div class="product__option product__description">
          <h2 class="bordered-bottom">
            {{ themeData('productDescriptionTitle') }}
          </h2>
          <div class="product__description-body">
            <div class="product__description-short mb-sm">
              <template v-for="(item, index) of shortDescription" :key="index">
                <span>{{ item.key }}</span>
                <span>{{ item.value }}</span>
              </template>
            </div>
            <div
              class="product__description-long"
              v-html="longDescription"
            ></div>
          </div>
        </div>
      </LightBoxPopup>
    </template>
    <template v-else>
      <h2 class="bordered-bottom">
        <template v-if="productStore.product">
          {{ themeData('productDescriptionTitle') }}
          {{
            shouldLoadProductDescriptionAndShippingByVariantType &&
            selectedVariantStyleOptionValue
              ? `for ${selectedVariantStyleOptionValue}`
              : ''
          }}
        </template>
        <sib-skeleton-loading v-else width="10em"></sib-skeleton-loading>
      </h2>
      <template v-if="isYourDesignProductType || isDigitalProductProductType">
        <div class="product__description-body">
          <div
            class="product__description-long"
            v-html="productStore.product?.descriptionHtml"
          ></div>
        </div>
      </template>
      <template v-else>
        <div class="product__description-body">
          <div v-if="loading">
            <sib-skeleton-loading
              v-for="index in 3"
              :key="index"
            ></sib-skeleton-loading>
          </div>
          <template v-else>
            <div class="product__description-short mb-sm">
              <template v-if="shortDescription">
                <template
                  v-for="(item, index) of shortDescription"
                  :key="index"
                >
                  <span>{{ item.key }}</span>
                  <span>{{ item.value }}</span>
                </template></template
              >
              <div
                v-else
                class="product__description-long"
                v-html="longDescription"
              ></div>
            </div>

            <!-- Popup for mobile -->
            <LightBoxPopup
              group="product-description"
              contentId="product-description-popup-content"
              class="hidden-md-up"
              v-if="shortDescription"
            >
              <template #trigger>
                <span
                  class="link hidden-md-up"
                  @click="trackingProductDescriptionDetailsOpened(false)"
                  >Details</span
                >
              </template>
              <div class="product__option product__description">
                <h2 class="bordered-bottom">
                  {{ themeData('productDescriptionTitle') }}
                </h2>
                <div class="product__description-body">
                  <div class="product__description-short mb-sm">
                    <template
                      v-for="(item, index) of shortDescription"
                      :key="index"
                    >
                      <span>{{ item.key }}</span>
                      <span>{{ item.value }}</span>
                    </template>
                  </div>
                  <div
                    class="product__description-long"
                    v-html="longDescription"
                  ></div>
                </div>
              </div>
            </LightBoxPopup>

            <!-- Collapsible for desktop -->
            <ProductCollapsible
              class="hidden-md-down"
              v-if="shortDescription"
              @toggle="trackingProductDescriptionDetailsOpened"
            >
              <div
                class="product__description-long"
                v-html="longDescription"
              ></div>
            </ProductCollapsible>
          </template>
        </div>
      </template>
    </template>
  </div>
</template>
<script lang="ts" setup>
import useProductStore from '@/store/product'
import { onMounted, watch, ref, Ref } from 'vue'
import {
  extractProductSKUFromHandle,
  getCustomPersonalization,
  isPersonalizeProduct,
  isProductHasTags,
  isProductHasType,
} from '@/utils/product'
import { computed } from '@vue/reactivity'
import ProductCollapsible from './ProductCollapsible.vue'
import { ProductDescription } from '@/types'
import Logger from '@/services/log'
import Analytics from '@/services/analytics'
import LightBoxPopup from '@/ui-kits/LightBoxPopup.vue'
import {
  QUERY_STRING_NO_CACHE,
  EVENT_TRACKING_SHOW_PRODUCT_DESCRIPTION,
  TIME_MARK_CSR_LOAD_PAGE,
  TIME_MARK_SHOW_PRODUCT_DESCRIPTION,
  CSR_LOAD_TYPE,
  MEASURE_NAME_PRODUCT_DESCRIPTION_LOADED,
  EVENT_TRACKING_PRODUCT_DESCRIPTION_DETAILS_OPENED,
  MIXPANEL_DESTINATION,
  destinationIntegrations,
  PRODUCT_LONG_DESCRIPTION_SELECTOR,
  PRODUCT_TYPE_YOUR_DESIGN,
  PRODUCT_TYPE_DIGITAL_PRODUCT,
} from '@/utils'
import { getElementhWithText } from '@/utils/dom'
import { useRoute } from 'vue-router'
import useRouteStore from '@/store/route'
import useSettingStore from '@/store/setting'
import { themeData } from '@/themes'

performance.mark(TIME_MARK_SHOW_PRODUCT_DESCRIPTION)

const route = useRoute()
const routeStore = useRouteStore()
const productStore = useProductStore()
const settingStore = useSettingStore()

const PRODUCT_TYPE_GIFT_SET = 'Gift Set'

const shortDescription = computed(() => {
  try {
    return productStore.productDescription?.short_description
      ? JSON.parse(productStore.productDescription.short_description)
      : ''
  } catch (error: any) {
    Logger.error('error when JSON parsing product description', {
      handle: productStore.product?.handle,
      productDescription: productStore.productDescription?.short_description,
      error: error,
    })
    Analytics.error(error)
    return null
  }
})

const isYourDesignProductType = computed(() => {
  return isProductHasType(productStore?.product, [PRODUCT_TYPE_YOUR_DESIGN])
})

const isDigitalProductProductType = computed(() => {
  return isProductHasType(productStore?.product, [PRODUCT_TYPE_DIGITAL_PRODUCT])
})

const longDescription = computed(() => {
  // if product type is 'Gift Set' so we will use shopify product description
  if (productStore.product?.productType == PRODUCT_TYPE_GIFT_SET) {
    return (
      productStore.product?.descriptionHtml ||
      productStore.productDescription?.description
    )
  }

  return (
    productStore.productDescription?.description ||
    productStore.product?.descriptionHtml
  )
})

const shouldLoadProductDescriptionAndShippingByVariantType = computed(
  () => productStore.shouldLoadProductDescriptionAndShippingByVariantType
)

const selectedVariantStyleOptionValue = computed(
  () => productStore.selectedVariantStyleOptionValue
)

const isPersonalize = computed(() => {
  return isPersonalizeProduct(
    productStore.product,
    settingStore.shop?.productTagPersonalize || ''
  )
})

const isCustomPersonalization = computed(() => {
  const customPersonalizations =
    settingStore.productPage?.customPersonalizations
  return !!getCustomPersonalization(
    productStore.product,
    customPersonalizations
  )
})

const isCustomForm = computed(() => {
  return isProductHasTags(productStore.product, [
    settingStore.shop?.productTagCustomForm || '',
  ])
})

const isProductTypeHiddenNotice = computed(() => {
  const productTypesHiddenNote =
    settingStore.shop?.productPersonalizationNoPreviewNote
      ?.productTypesHiddenNote || []
  return isProductHasType(productStore?.product, productTypesHiddenNote)
})

const productDescriptionMap: Ref<{
  [key: string]: ProductDescription
}> = ref({})
const loading = ref(true)

onMounted(() => {
  watch(
    () => productStore.product?.id,
    async (newVal, oldVal) => {
      if (newVal !== oldVal && newVal) {
        if (productStore.shouldLoadProductDescriptionAndShippingByVariantType) {
          watch(
            () => productStore.selectedVariantStyleOptionValue,
            async () => {
              if (!productStore.selectedVariantStyleOptionValue) return
              await fetchProductDescription()
              trackingShowProductDescription()
            },
            { immediate: true }
          )
        } else {
          await fetchProductDescription()
          trackingShowProductDescription()
        }
      }
    },
    { immediate: true }
  )
  watch(
    () => longDescription.value,
    () => {
      if (!isProductTypeHiddenNotice.value) {
        setTimeout(productPersonalizationNoPreviewDescriptionHandler)
      }
    }
  )
})

async function fetchProductDescription() {
  loading.value = true
  if (!productStore.product || isDigitalProductProductType.value) return
  const code = extractProductSKUFromHandle(productStore.product.handle)
  const tag = productStore.product?.tags?.join(',')
  const type =
    (productStore.shouldLoadProductDescriptionAndShippingByVariantType
      ? productStore.selectedVariantStyleOptionValue
      : productStore.product?.productType) || ''

  if (
    productDescriptionMap.value[buildProductDescriptionKey({ code, type, tag })]
  ) {
    loading.value = false
    productStore.productDescription =
      productDescriptionMap.value[
        buildProductDescriptionKey({ code, type, tag })
      ]
    return
  }

  const description = await productStore.getProductDescription({
    code,
    type: type.endsWith('.') ? type.slice(0, -1) : type,
    tag,
    isForceLoadNewData: !!route.query[QUERY_STRING_NO_CACHE],
  })
  loading.value = false
  if (description) {
    productDescriptionMap.value[
      buildProductDescriptionKey({ code, type, tag })
    ] = description
  }
}

// replace text Personalization in product desciption for product personalize no preview
function productPersonalizationNoPreviewDescriptionHandler() {
  if (
    (!isCustomPersonalization.value && !isCustomForm.value) ||
    !isPersonalize.value
  )
    return

  const productLongDescriptions = document.querySelectorAll(
    PRODUCT_LONG_DESCRIPTION_SELECTOR
  )
  if (!productLongDescriptions.length) return

  productLongDescriptions.forEach((productLongDescription) => {
    const element = getElementhWithText(
      productLongDescription,
      'p',
      'Personalization'
    )
    if (!element) return

    const nextElement = element.nextElementSibling
    if (!nextElement) return
    if (nextElement.nextElementSibling) {
      nextElement.nextElementSibling.classList.add('hidden')
    }
    nextElement.classList.add('hidden')
    const productPersonalizationNoPreviewDescription =
      document.createElement('div')
    productPersonalizationNoPreviewDescription.innerHTML =
      settingStore.shop?.productPersonalizationNoPreviewDescription || ''

    productLongDescription.replaceChild(
      productPersonalizationNoPreviewDescription,
      element
    )
  })
}

function trackingShowProductDescription() {
  if (!productStore.product?.handle) return
  const performanceTimeProdudctDescriptionLoaded =
    routeStore.loadType == CSR_LOAD_TYPE
      ? performance.measure(
          MEASURE_NAME_PRODUCT_DESCRIPTION_LOADED,
          TIME_MARK_CSR_LOAD_PAGE
        ).duration
      : performance.now()
  const selfTimeProdudctDescriptionLoaded = performance.measure(
    MEASURE_NAME_PRODUCT_DESCRIPTION_LOADED,
    TIME_MARK_SHOW_PRODUCT_DESCRIPTION
  ).duration

  Analytics.track(
    EVENT_TRACKING_SHOW_PRODUCT_DESCRIPTION,
    {
      performance_time: performanceTimeProdudctDescriptionLoaded,
      self_time: selfTimeProdudctDescriptionLoaded,
      handle: productStore.product.handle,
      load_type: routeStore.loadType,
    },
    destinationIntegrations([MIXPANEL_DESTINATION])
  )
}

function trackingProductDescriptionDetailsOpened(isCollapsed: boolean) {
  if (!productStore.product || isCollapsed) return
  Analytics.track(
    EVENT_TRACKING_PRODUCT_DESCRIPTION_DETAILS_OPENED,
    {
      handle: productStore.product.handle,
      tags: productStore.product.tags,
      path: route.path,
    },
    destinationIntegrations([MIXPANEL_DESTINATION])
  )
}

function buildProductDescriptionKey({
  code,
  type,
  tag,
}: {
  code: string
  type?: string
  tag?: string
}): string {
  return `${code || ''}-${type || ''}-${tag || ''}`
}
</script>

<style lang="scss">
.lightbox-popup__content .product__option {
  margin: 0;
}

.product__description {
  .product-description-new-ui-desktop .collapsible__header {
    font-size: 16px;
    font-weight: 600;
    color: unset;
    background: unset;
    padding-left: 0;
  }
  .product-description-new-ui-mobile {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    text-decoration: unset;
    font-size: 16px;
    font-weight: 600;
    color: unset;
    background: unset;
    padding-left: 0;
    color: #212529 !important;
  }
  &-short {
    display: grid;
    grid-template-columns: auto 1fr;
    gap: 1em;
  }

  .link {
    color: var(--color-primary);
  }

  ul,
  li {
    margin: 0.5em;
  }

  @include media-lg-up {
    &-short {
      gap: 1em 2em;
    }
  }

  @include media-xl-up {
    &-short {
      gap: 1em 3em;
    }
  }
}
</style>
